import { Component } from '@theme/component';

/**
 * Newsletter signup component. Keeps the hidden `contact[tags]` field in sync
 * with the checked interest checkboxes as a comma-separated list, since Shopify's
 * form parser only keeps the last value when the same name appears multiple times.
 *
 * The form itself submits natively — Shopify's bot protection (CAPTCHA + CSRF)
 * requires a native submission; AJAX POSTs get rejected with 400/401. Success
 * and error states are rendered server-side via `form.posted_successfully?`
 * and `form.errors` in the Liquid template.
 */
class HalueNewsletter extends Component {
  /** @type {HTMLInputElement|null} */
  #tagsInput = null;

  /** Milliseconds the success confirmation stays before fading out. */
  #confirmationDuration = 10000;

  connectedCallback() {
    super.connectedCallback();

    this.#tagsInput = this.querySelector('[data-halue-newsletter-tags]');

    for (const checkbox of this.querySelectorAll('.halue-newsletter__interest-input')) {
      checkbox.addEventListener('change', this.#syncTags.bind(this));
    }

    const form = this.querySelector('form');
    if (form) form.addEventListener('submit', this.#syncTags.bind(this));

    this.#revealConfirmation();
  }

  /**
   * After a successful native submit the page reloads with the server-rendered
   * success message in the DOM. Replace the form with that message, then fade it
   * out and restore the pristine form. The success message is focused for screen
   * readers (the markup already carries `tabindex="-1"`). The error state is left
   * untouched so the visitor can correct their input.
   */
  #revealConfirmation() {
    const success = /** @type {HTMLElement|null} */ (
      this.querySelector('[id^="HalueNewsletter-success"]')
    );
    if (!success) return;

    this.classList.add('halue-newsletter--submitted');
    success.focus();

    setTimeout(() => {
      this.classList.add('halue-newsletter--dismissing');
      const done = () => {
        this.classList.remove('halue-newsletter--submitted', 'halue-newsletter--dismissing');
        success.remove();
      };
      const message = success.closest('.halue-newsletter__message') || success;
      message.addEventListener('transitionend', done, { once: true });
      // Fallback if no transition runs (e.g. reduced motion).
      setTimeout(done, 600);
    }, this.#confirmationDuration);
  }

  /**
   * Rebuilds the `contact[tags]` value as a comma-separated list of:
   * - `newsletter` (always)
   * - `market-{country}` if the Liquid template injected a country code
   * - any checked interest values
   */
  #syncTags() {
    if (!this.#tagsInput) return;
    const checked = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('.halue-newsletter__interest-input:checked')
    );
    const market = (this.dataset.market || '').toLowerCase();
    const tags = [
      'newsletter',
      market ? `market-${market}` : null,
      ...Array.from(checked, (input) => input.value)
    ].filter(Boolean);
    this.#tagsInput.value = tags.join(',');
  }
}

customElements.define('halue-newsletter', HalueNewsletter);
